import { ComponentProps, JSXElementConstructor } from 'react';

import { nodeLabelForTrigger } from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree';
import {
  GAP_STEP_CARDS_HORIZONTAL,
  NODE_WIDTH,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/constants';
import NodeModuleBoundary from 'dashboard/components/WorkflowEditor/WorkflowCanvas/NodeModuleBoundary';
import NodePlusButton from 'dashboard/components/WorkflowEditor/WorkflowCanvas/NodePlusButton';
import NodeRouteName from 'dashboard/components/WorkflowEditor/WorkflowCanvas/NodeRouteName';
import {
  NodeStepInput,
  NodeStepModule,
  NodeStepOutput,
  NodeStepPlaceholder,
  NodeStepWithBranch,
  NodeStepWithoutBranch,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/NodeStep';
import {
  KeyedGraph,
  StepGraphExtraType,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/step-graph';
import { WorkflowRunStepDisplay } from 'dashboard/components/WorkflowEditor/WorkflowCanvas/utils';
import FlowPlotter, {
  BaseNodeProps,
  NodeInteractionMode,
} from 'dashboard/components-lib/FlowPlotter';
import { FakeWorkflowStepType, WorkflowStepType } from 'dashboard/models/workflow-version';
import { assertNever } from 'lib/utils/types';

type FlowPlotterProps = ComponentProps<typeof FlowPlotter>;

enum NodeComponent {
  ConditionalRoute = 'conditional-route',
  ModuleBoundary = 'module-boundary',
  PlusButton = 'plus-button',
  StepInput = 'step-input',
  StepOutput = 'step-output',
  StepModule = 'step-module',
  StepPlaceholder = 'step-placeholder',
  StepWithBranch = 'step-with-branch',
  StepWithoutBranch = 'step-without-branch',
}

export const nodeComponents: FlowPlotterProps['nodeComponents'] = {
  [NodeComponent.ConditionalRoute]: NodeRouteName,
  [NodeComponent.ModuleBoundary]: NodeModuleBoundary,
  [NodeComponent.PlusButton]: NodePlusButton,
  [NodeComponent.StepOutput]: NodeStepOutput,
  [NodeComponent.StepModule]: NodeStepModule,
  [NodeComponent.StepPlaceholder]: NodeStepPlaceholder,
  [NodeComponent.StepInput]: NodeStepInput,
  [NodeComponent.StepWithBranch]: NodeStepWithBranch,
  [NodeComponent.StepWithoutBranch]: NodeStepWithoutBranch,
};

interface NodeRenderingContext {
  deactivatedNodeTokens: string[];
  editable: boolean;
  erroredNodeTokens: string[];
  expandedModuleStepTokens: string[];
  focusedNodeToken: string | null;
  selectedNodeToken: string | null;
  tokenToRunStepDisplay: Record<string, WorkflowRunStepDisplay>;
}

type CustomComponentPropsFor<T extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>> =
  Omit<ComponentProps<T>, keyof BaseNodeProps>;

type ComponentKeyAndPropsBuilder = (node: KeyedGraph[string]) => {
  componentKey: NodeComponent;
  componentProps: Record<string, unknown>;
};

const getComponentKeyAndPropsBuilder =
  ({
    deactivatedNodeTokens,
    editable,
    erroredNodeTokens,
    focusedNodeToken,
    selectedNodeToken,
    tokenToRunStepDisplay,
  }: NodeRenderingContext): ComponentKeyAndPropsBuilder =>
  ({ step, moduleToken }) => {
    switch (step.type) {
      case WorkflowStepType.Action:
      case WorkflowStepType.Wait:
      case FakeWorkflowStepType.MapOutput:
        return {
          componentKey: NodeComponent.StepWithoutBranch,
          componentProps: {
            name: step.name,
            deactivated: deactivatedNodeTokens.includes(step.token),
            errored: erroredNodeTokens.includes(step.token),
            focused: focusedNodeToken === step.token,
            selected: selectedNodeToken === step.token,
            run: tokenToRunStepDisplay[step.token] ?? null,
            type: step.type,
          } satisfies CustomComponentPropsFor<typeof NodeStepWithoutBranch>,
          interactionMode:
            !editable || !!moduleToken || step.type === FakeWorkflowStepType.MapOutput
              ? NodeInteractionMode.Disabled
              : NodeInteractionMode.Drag,
        };
      case WorkflowStepType.Conditional:
      case WorkflowStepType.Loop:
      case WorkflowStepType.Parallel:
        return {
          componentKey: NodeComponent.StepWithBranch,
          componentProps: {
            name: step.name,
            collapsed: step.collapsed,
            errored: erroredNodeTokens.includes(step.token),
            focused: focusedNodeToken === step.token,
            selected: selectedNodeToken === step.token,
            run: tokenToRunStepDisplay[step.token] ?? null,
            type: step.type,
          } satisfies CustomComponentPropsFor<typeof NodeStepWithBranch>,
          interactionMode:
            !editable || !!moduleToken ? NodeInteractionMode.Disabled : NodeInteractionMode.Drag,
        };
      case WorkflowStepType.Module:
        return {
          componentKey: NodeComponent.StepModule,
          componentProps: {
            name: step.name,
            collapsed: step.collapsed,
            expandable: step.expandable,
            errored: erroredNodeTokens.includes(step.token),
            focused: focusedNodeToken === step.token,
            selected: selectedNodeToken === step.token,
            run: tokenToRunStepDisplay[step.token] ?? null,
          } satisfies CustomComponentPropsFor<typeof NodeStepModule>,
          interactionMode: editable ? NodeInteractionMode.Drag : NodeInteractionMode.Disabled,
        };
      case WorkflowStepType.Placeholder:
        return {
          componentKey: NodeComponent.StepPlaceholder,
          componentProps: {
            errored: erroredNodeTokens.includes(step.token),
            selected: selectedNodeToken === step.token,
          } satisfies CustomComponentPropsFor<typeof NodeStepPlaceholder>,
        };
      case StepGraphExtraType.ConditionalRoute:
        return {
          componentKey: NodeComponent.ConditionalRoute,
          componentProps: {
            active: selectedNodeToken === step.conditionalStepToken,
            parentStepToken: step.conditionalStepToken,
            routeLabel: step.title,
            routeIndex: step.routeIndex,
            status: tokenToRunStepDisplay[step.token]?.status ?? null,
          } satisfies CustomComponentPropsFor<typeof NodeRouteName>,
        };
      case StepGraphExtraType.PlusButton:
        return {
          componentKey: NodeComponent.PlusButton,
          componentProps: {
            clickPayload: step.clickPayload,
            disabled: !editable || !!moduleToken,
          } satisfies CustomComponentPropsFor<typeof NodePlusButton>,
          interactionMode:
            !editable || !!moduleToken ? NodeInteractionMode.Disabled : NodeInteractionMode.Drop,
        };
      case WorkflowStepType.Output:
        return {
          componentKey: NodeComponent.StepOutput,
          componentProps: {
            name: step.name,
            deactivated: deactivatedNodeTokens.includes(step.token),
            errored: erroredNodeTokens.includes(step.token),
            focused: focusedNodeToken === step.token,
            selected: selectedNodeToken === step.token,
            run: tokenToRunStepDisplay[step.token] ?? null,
          } satisfies CustomComponentPropsFor<typeof NodeStepOutput>,
        };
      case WorkflowStepType.Input: {
        const { trigger } = step;
        const nameProps = trigger
          ? {
              name: nodeLabelForTrigger(trigger),
              typeName: 'Trigger',
            }
          : {
              name: 'Input Data',
              typeName: 'Input',
            };

        return {
          componentKey: NodeComponent.StepInput,
          componentProps: {
            ...nameProps,
            errored: erroredNodeTokens.includes(step.token),
            focused: focusedNodeToken === step.token,
            selected: selectedNodeToken === step.token,
            run: tokenToRunStepDisplay[step.token] ?? null,
          } satisfies CustomComponentPropsFor<typeof NodeStepInput>,
        };
      }
      default:
        assertNever(step);
    }
  };

const HEIGHT_NODE_STEP_CARD = 68;
const HEIGHT_NODE_BRANCH_PILL = 32;
const HEIGHT_NODE_ADD_BUTTON = 24;
const GAP_STEP_CARDS_VERTICAL = 4;
const GAP_AFTER_CHANGING_BISECTOR_VERTICAL = 48;
const GAP_AROUND_MODULE = 16;

const buildNodesByWalkGraph = ({
  graph: wholeGraph,
  initialKey,
  initialNodeY,
  initialNodeBisectorX,
  initialBranchRoot,
  getComponentKeyAndProps,
}: {
  graph: KeyedGraph;
  initialKey: string;
  initialNodeY: number;
  initialNodeBisectorX: number;
  initialBranchRoot: string;
  getComponentKeyAndProps: ComponentKeyAndPropsBuilder;
}) => {
  const { ...remainingGraph } = wholeGraph;
  const bisectorForBranchRoot: Record<string, number> = {
    [wholeGraph[initialKey].branchRoot]: initialNodeBisectorX,
  };

  const walkGraph = ({
    currentKey,
    previousDepthAfterNode,
    previousBranchRoot,
  }: {
    currentKey: string;
    previousDepthAfterNode: number;
    previousBranchRoot: string;
  }): FlowPlotterProps['nodes'][number][] => {
    const currentGraphNode = remainingGraph[currentKey];
    delete remainingGraph[currentKey];
    if (currentGraphNode == null) return [];

    const currentBranchRoot = currentGraphNode.branchRoot;
    const currentDepth =
      previousDepthAfterNode +
      (previousBranchRoot === currentBranchRoot
        ? GAP_STEP_CARDS_VERTICAL
        : GAP_AFTER_CHANGING_BISECTOR_VERTICAL) +
      (currentGraphNode.step.type === WorkflowStepType.Module && !currentGraphNode.step.collapsed
        ? GAP_AROUND_MODULE
        : 0);

    const currentNode: FlowPlotterProps['nodes'][number] = {
      id: currentGraphNode.token,
      x: bisectorForBranchRoot[currentBranchRoot] - NODE_WIDTH / 2,
      y: currentDepth,
      ...getComponentKeyAndProps(currentGraphNode),
    };

    const branching =
      [WorkflowStepType.Conditional, WorkflowStepType.Parallel].some(
        (type) => type === currentGraphNode.step.type,
      ) && !(currentGraphNode.step as { collapsed: boolean }).collapsed;
    const shifting =
      currentGraphNode.step.type === WorkflowStepType.Loop && !currentGraphNode.step.collapsed;

    const nextNodes =
      !branching && !shifting && !currentGraphNode.converging
        ? walkGraph({
            currentKey: currentGraphNode.adjacents[0],
            previousDepthAfterNode:
              currentDepth +
              (currentGraphNode.step.type === StepGraphExtraType.ConditionalRoute
                ? HEIGHT_NODE_BRANCH_PILL
                : currentGraphNode.step.type === StepGraphExtraType.PlusButton
                  ? HEIGHT_NODE_ADD_BUTTON
                  : currentGraphNode.step.type === WorkflowStepType.Output
                    ? HEIGHT_NODE_STEP_CARD + GAP_AROUND_MODULE
                    : HEIGHT_NODE_STEP_CARD),
            previousBranchRoot: currentBranchRoot,
          })
        : [];

    const shiftedNodes = shifting
      ? (() => {
          if (currentGraphNode.step.type === WorkflowStepType.Loop) {
            bisectorForBranchRoot[currentGraphNode.adjacents[0]] =
              bisectorForBranchRoot[currentBranchRoot] +
              (NODE_WIDTH + GAP_STEP_CARDS_HORIZONTAL) / 2;
          }
          const loopedNodes = walkGraph({
            currentKey: currentGraphNode.adjacents[0],
            previousDepthAfterNode: currentDepth + HEIGHT_NODE_STEP_CARD,
            previousBranchRoot: currentBranchRoot,
          });

          const lastInLoop = loopedNodes.slice(-1)[0];
          const lastNode = wholeGraph[lastInLoop.id];
          const convergedStep = wholeGraph[lastNode?.adjacents[0]];

          return [
            ...loopedNodes,
            ...(lastNode && convergedStep
              ? walkGraph({
                  currentKey: convergedStep.token,
                  previousDepthAfterNode:
                    lastInLoop.y +
                    (lastNode.step.type === StepGraphExtraType.PlusButton
                      ? HEIGHT_NODE_ADD_BUTTON
                      : HEIGHT_NODE_STEP_CARD),
                  previousBranchRoot: lastNode.branchRoot,
                })
              : []),
          ];
        })()
      : [];

    const branchedNodes = branching
      ? (() => {
          const totalBreadths = currentGraphNode.adjacentBreadths.reduce((sum, n) => sum + n, 0);

          const leftEdgeBisector =
            bisectorForBranchRoot[currentGraphNode.branchRoot] -
            (totalBreadths * (NODE_WIDTH + GAP_STEP_CARDS_HORIZONTAL) - GAP_STEP_CARDS_HORIZONTAL) /
              2 +
            GAP_STEP_CARDS_HORIZONTAL;
          const branches = currentGraphNode.adjacentBreadths.map((c, index) => {
            // Calculate the position of the bisector line while preserving the width of nested child elements
            const branchRootBisector =
              leftEdgeBisector +
              (currentGraphNode.adjacentBreadths.slice(0, index).reduce((sum, n) => sum + n, 0) *
                (NODE_WIDTH + GAP_STEP_CARDS_HORIZONTAL) -
                GAP_STEP_CARDS_HORIZONTAL) +
              (c * (NODE_WIDTH + GAP_STEP_CARDS_HORIZONTAL) - GAP_STEP_CARDS_HORIZONTAL) / 2;

            bisectorForBranchRoot[currentGraphNode.adjacents[index]] = branchRootBisector;
            return walkGraph({
              currentKey: currentGraphNode.adjacents[index],
              previousDepthAfterNode: currentDepth + HEIGHT_NODE_STEP_CARD,
              previousBranchRoot: currentBranchRoot,
            });
          });

          const lastsInBranch = branches.map((branch) => branch.slice(-1)[0]);
          const sampleLast = wholeGraph[lastsInBranch[0].id];
          const convergedStep = wholeGraph[sampleLast?.adjacents[0] ?? ''];
          const shouldRenderStepConverged = () =>
            Object.values(remainingGraph).find(function search({ adjacents }) {
              return adjacents[0] === (convergedStep?.token ?? '');
            });

          return [
            ...branches.flat(),
            ...(sampleLast && convergedStep && shouldRenderStepConverged() == null
              ? walkGraph({
                  currentKey: convergedStep.token,
                  previousDepthAfterNode: Math.max(...lastsInBranch.map(({ y }) => y)),
                  previousBranchRoot: sampleLast.branchRoot,
                })
              : []),
          ];
        })()
      : [];

    return [currentNode, ...nextNodes, ...branchedNodes, ...shiftedNodes];
  };

  return walkGraph({
    currentKey: initialKey,
    previousDepthAfterNode: initialNodeY,
    previousBranchRoot: initialBranchRoot,
  });
};

export const nodesFromGraph = (
  keyedGraph: KeyedGraph,
  initialKey: string,
  context: NodeRenderingContext,
): FlowPlotterProps['nodes'] => {
  const nodes = buildNodesByWalkGraph({
    graph: keyedGraph,
    initialKey,
    initialNodeY: 60,
    initialNodeBisectorX: 700,
    initialBranchRoot: keyedGraph[initialKey].branchRoot,
    getComponentKeyAndProps: getComponentKeyAndPropsBuilder(context),
  });

  const moduleBoundaries = context.expandedModuleStepTokens
    .map<FlowPlotterProps['nodes'][number] | null>((moduleStepToken) => {
      const moduleStep = keyedGraph[moduleStepToken]?.step;
      if (moduleStep?.type === WorkflowStepType.Module && !moduleStep.collapsed) {
        const firstModuleNode = nodes.find(({ id }) => id === moduleStepToken);
        const lastModuleNode = nodes.find(({ id }) => id === moduleStep.outputNodeToken);

        if (!firstModuleNode || !lastModuleNode) return null;

        const width =
          NODE_WIDTH * moduleStep.moduleBreadths +
          GAP_STEP_CARDS_HORIZONTAL * (moduleStep.moduleBreadths - 1) +
          GAP_AROUND_MODULE * 2;

        return {
          id: `boundary-${moduleStepToken}`,
          x: firstModuleNode.x + NODE_WIDTH / 2 - width / 2,
          y: firstModuleNode.y - GAP_AROUND_MODULE,
          z: 4,
          componentKey: NodeComponent.ModuleBoundary,
          componentProps: {
            errored: context.erroredNodeTokens.includes(moduleStepToken),
            runStepStatus: context.tokenToRunStepDisplay[moduleStepToken]?.status ?? null,
            selected: context.selectedNodeToken === moduleStepToken,
            stepToken: moduleStepToken,
            height:
              lastModuleNode.y + HEIGHT_NODE_STEP_CARD - firstModuleNode.y + GAP_AROUND_MODULE * 2,
            width,
          } satisfies CustomComponentPropsFor<typeof NodeModuleBoundary>,
          interactionMode: NodeInteractionMode.Disabled,
        };
      }
      return null;
    })
    .filter<FlowPlotterProps['nodes'][number]>(
      (node): node is FlowPlotterProps['nodes'][number] => node != null,
    );

  return [...nodes, ...moduleBoundaries];
};
